import {
  EncumbranceExcelGroup,
  EncumbranceExcelPartnerRef,
  EncumbranceExcelRow,
  EncumbrancesExcelValidationErrorCode,
} from '../types/EncumbrancesExcel';

const HISTORICAL_ADMISSIBLE_ERROR_CODES = new Set<EncumbrancesExcelValidationErrorCode>(
  ['RANGE_NOT_AVAILABLE']
);

const hasNonHistoricalAdmissibleErrors = (
  group: EncumbranceExcelGroup
): boolean =>
  group.errors.some(
    (error) => !HISTORICAL_ADMISSIBLE_ERROR_CODES.has(error.code)
  );

export const isEndDateBeforeToday = (endDate: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(0, 0, 0, 0);

  return normalizedEndDate < today;
};

export const isHistoricalEligible = (group: EncumbranceExcelGroup): boolean =>
  !group.isValid &&
  Boolean(group.endDate) &&
  isEndDateBeforeToday(group.endDate as Date) &&
  Boolean(group.resolvedHolderPartnerId) &&
  !hasNonHistoricalAdmissibleErrors(group);

export const isImportableGroup = (
  group: EncumbranceExcelGroup,
  uploadInformationalWithErrors: boolean
): boolean => {
  if (group.isValid) {
    return true;
  }

  if (uploadInformationalWithErrors && isHistoricalEligible(group)) {
    return true;
  }

  return false;
};

export const isBlockingGroup = (
  group: EncumbranceExcelGroup,
  uploadInformationalWithErrors: boolean
): boolean => !isImportableGroup(group, uploadInformationalWithErrors);

export type EncumbrancesBulkPartyPayload = {
  partner?: string;
  name: string;
  cif: string;
  email?: string;
  percentage: number;
  comment: string;
};

export type EncumbrancesBulkItemPayload = {
  encumbranceId: string;
  type: string;
  date: Date;
  endDate: Date | null;
  from: number;
  to: number;
  partner: string;
  share: string | null;
  comment: string;
  parties: EncumbrancesBulkPartyPayload[];
};

const mapPartyRowToPayload = (
  row: EncumbranceExcelRow,
  partnerByCif: Map<string, EncumbranceExcelPartnerRef>
): EncumbrancesBulkPartyPayload => {
  const resolvedPartner =
    row.partyCif && row.partyCif !== 'NA'
      ? partnerByCif.get(row.partyCif)?._id
      : undefined;

  const payload: EncumbrancesBulkPartyPayload = {
    partner: resolvedPartner,
    name: row.partyName || '',
    cif: row.partyCif || '',
    percentage: row.percentage ?? 0,
    comment: row.comments || '',
  };

  if (row.partyEmail) {
    payload.email = row.partyEmail;
  }

  return payload;
};

export const mapGroupToBulkItem = (
  group: EncumbranceExcelGroup,
  partnerByCif: Map<string, EncumbranceExcelPartnerRef>,
  uploadInformationalWithErrors: boolean
): EncumbrancesBulkItemPayload | null => {
  if (!isImportableGroup(group, uploadInformationalWithErrors)) {
    return null;
  }

  const isHistorical =
    !group.isValid &&
    uploadInformationalWithErrors &&
    isHistoricalEligible(group);

  if (!group.resolvedHolderPartnerId) {
    return null;
  }

  if (!isHistorical && !group.resolvedShareId) {
    return null;
  }

  return {
    encumbranceId: group.encumbranceId,
    type: group.type,
    date: group.date,
    endDate: group.endDate,
    from: group.shareFrom,
    to: group.shareTo,
    partner: group.resolvedHolderPartnerId,
    share: isHistorical ? null : group.resolvedShareId || null,
    comment: group.holderRow?.comments || '',
    parties: group.partyRows.map((row) =>
      mapPartyRowToPayload(row, partnerByCif)
    ),
  };
};

export const buildEncumbrancesBulkPayload = ({
  groups,
  partnerByCif,
  uploadInformationalWithErrors,
}: {
  groups: EncumbranceExcelGroup[];
  partnerByCif: Map<string, EncumbranceExcelPartnerRef>;
  uploadInformationalWithErrors: boolean;
}): EncumbrancesBulkItemPayload[] =>
  groups
    .map((group) =>
      mapGroupToBulkItem(group, partnerByCif, uploadInformationalWithErrors)
    )
    .filter((item): item is EncumbrancesBulkItemPayload => Boolean(item));

export const getEncumbrancesExcelImportSummary = ({
  groups,
  uploadInformationalWithErrors,
}: {
  groups: EncumbranceExcelGroup[];
  uploadInformationalWithErrors: boolean;
}) => {
  const validCount = groups.filter((group) => group.isValid).length;
  const historicalCount = uploadInformationalWithErrors
    ? groups.filter((group) => isHistoricalEligible(group)).length
    : 0;
  const importableCount = groups.filter((group) =>
    isImportableGroup(group, uploadInformationalWithErrors)
  ).length;
  const blockingCount = groups.filter((group) =>
    isBlockingGroup(group, uploadInformationalWithErrors)
  ).length;

  return {
    validCount,
    historicalCount,
    importableCount,
    blockingCount,
  };
};
